import type { AssayFloor, AssayItem, AssayLink } from '../api'
import { bandOffset, timeTicks, type TimeDomain } from './scale'

/**
 * The chronology — a canvas, drawn on demand.
 *
 * ## The one figure this console is built around
 *
 * Every item is a DUMBBELL: a filled mark at the date the thing happened, an open mark at the
 * date this platform took custody of it, and a hairline joining them. The length of that
 * hairline is how late our knowledge was, and it is the only place in the product where that
 * quantity is visible at all. A dossier of short ligatures is watching; a dossier of long ones
 * is reading history. Both are legitimate; not knowing which you have is not.
 *
 * The vertical axis is standing — Admiralty distance from the brief floor, on the binding axis
 * — so the floor is a horizontal DATUM across the whole figure. Above it is what could be
 * quoted; below it, drawn over a hatched ground, is what is on file and cannot be. That rule
 * is the same one the plate draws as a staircase, and both come from the same server-computed
 * number so they can never disagree.
 *
 * ## There is no animation, and that is deliberate
 *
 * The war room's board sweeps and pulses because the thing it watches is moving and a still
 * frame there would be a lie about liveness. A record is not moving. It changes when somebody
 * files something. So this canvas has no frame loop at all: it draws when the data, the
 * filter, the pointer or the size changes, and otherwise it sits still — which is also what
 * makes it read as a printed plate rather than as a screen.
 */

export interface Rgb { r: number, g: number, b: number }

export interface Theme {
  paper: Rgb
  ink: Rgb
  inkDim: Rgb
  inkFaint: Rgb
  rule: Rgb
  ruleStrong: Rgb
  /** At or above the floor — the quotable register. */
  above: Rgb
  /** Below it. */
  below: Rgb
  dispute: Rgb
  corroborate: Rgb
}

const FALLBACK: Theme = {
  paper: { r: 250, g: 248, b: 244 },
  ink: { r: 41, g: 37, b: 33 },
  inkDim: { r: 106, g: 99, b: 91 },
  inkFaint: { r: 150, g: 142, b: 133 },
  rule: { r: 214, g: 207, b: 197 },
  ruleStrong: { r: 90, g: 82, b: 74 },
  above: { r: 32, g: 92, b: 122 },
  below: { r: 168, g: 84, b: 46 },
  dispute: { r: 178, g: 45, b: 40 },
  corroborate: { r: 44, g: 106, b: 138 },
}

/**
 * Resolve the CSS tokens to numbers, once.
 *
 * Canvas will accept an `oklch()` string, but it re-parses the colour on every draw call, and
 * this figure makes thousands of them. Each token is rasterised into a 1×1 canvas and read
 * back as RGB so every later use is a cheap `rgba()` template — and the stylesheet stays the
 * single place the palette is defined.
 */
export function readTheme(el: HTMLElement): Theme {
  const cs = getComputedStyle(el)
  const probe = document.createElement('canvas')
  probe.width = 1
  probe.height = 1
  const ctx = probe.getContext('2d', { willReadFrequently: true })
  const token = (name: string, fallback: Rgb): Rgb => {
    const value = cs.getPropertyValue(name).trim()
    if (!ctx || !value) return fallback
    // The black primer turns "this token is missing" into a visible, debuggable result:
    // an unparseable value leaves fillStyle at whatever was set last rather than throwing.
    ctx.fillStyle = '#000'
    ctx.fillStyle = value
    ctx.fillRect(0, 0, 1, 1)
    try {
      const d = ctx.getImageData(0, 0, 1, 1).data
      return { r: d[0] ?? 0, g: d[1] ?? 0, b: d[2] ?? 0 }
    } catch {
      return fallback
    }
  }
  return {
    paper: token('--plate-paper', FALLBACK.paper),
    ink: token('--plate-ink', FALLBACK.ink),
    inkDim: token('--plate-ink-dim', FALLBACK.inkDim),
    inkFaint: token('--plate-ink-faint', FALLBACK.inkFaint),
    rule: token('--plate-rule', FALLBACK.rule),
    ruleStrong: token('--plate-rule-strong', FALLBACK.ruleStrong),
    above: token('--plate-above', FALLBACK.above),
    below: token('--plate-below', FALLBACK.below),
    dispute: token('--plate-dispute', FALLBACK.dispute),
    corroborate: token('--plate-corroborate', FALLBACK.corroborate),
  }
}

export const rgba = (c: Rgb, a: number) => `rgba(${c.r},${c.g},${c.b},${a})`

export const MARGIN = { left: 62, right: 18, top: 16, bottom: 38 }

export interface ChronoScene {
  items: AssayItem[]
  links: AssayLink[]
  floor: AssayFloor
  domain: TimeDomain
  /** The custody window's left edge — drawn inside the figure, not as its frame. */
  windowFrom: number
  width: number
  height: number
  dpr: number
  theme: Theme
  /** Ids the active filter did NOT match. Null when nothing is filtering. */
  missed: Set<string> | null
  hovered: string | null
  selected: string | null
  showLigatures: boolean
  showLinks: boolean
}

export interface Placement { id: string, ex: number | null, cx: number, y: number }

/** Standing range, padded to a minimum so a uniform corpus is not drawn on one line. */
function extent(items: AssayItem[]): { lo: number, hi: number } {
  let lo = 0
  let hi = 0
  for (const item of items) {
    if (item.standing < lo) lo = item.standing
    if (item.standing > hi) hi = item.standing
  }
  return { lo: Math.min(lo, -1) - 0.6, hi: Math.max(hi, 1) + 0.6 }
}

/**
 * Where every item sits. Computed once and shared by the renderer and the hit test.
 *
 * Two projections that agree by construction rather than by both being written carefully: a
 * hit test that recomputed the layout is a hit test that drifts from the picture the moment
 * one of them is edited.
 */
export function placeItems(scene: ChronoScene): Placement[] {
  const { lo, hi } = extent(scene.items)
  const plotW = scene.width - MARGIN.left - MARGIN.right
  const plotH = scene.height - MARGIN.top - MARGIN.bottom
  const span = scene.domain.to - scene.domain.from || 1
  const x = (at: number) => MARGIN.left + ((at - scene.domain.from) / span) * plotW
  const band = plotH / (hi - lo)
  const y = (standing: number, id: string) =>
    MARGIN.top + ((hi - standing) / (hi - lo)) * plotH + bandOffset(id) * band * 0.34

  return scene.items.map((item) => {
    const collected = Date.parse(item.collectedAt)
    const occurred = item.occurredAt ? Date.parse(item.occurredAt) : null
    return {
      id: item.id,
      ex: occurred === null || !Number.isFinite(occurred) ? null : x(occurred),
      cx: x(collected),
      y: y(item.standing, item.id),
    }
  })
}

/** The y of a standing value, for the gridlines and the datum. */
function standingY(scene: ChronoScene, standing: number): number {
  const { lo, hi } = extent(scene.items)
  const plotH = scene.height - MARGIN.top - MARGIN.bottom
  return MARGIN.top + ((hi - standing) / (hi - lo)) * plotH
}

export function drawChronology(
  ctx: CanvasRenderingContext2D, scene: ChronoScene, places: Placement[],
) {
  const { theme: T, width: W, height: H, dpr } = scene
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, W, H)

  const plotW = W - MARGIN.left - MARGIN.right
  const datum = standingY(scene, 0)
  const span = scene.domain.to - scene.domain.from || 1
  const x = (at: number) => MARGIN.left + ((at - scene.domain.from) / span) * plotW

  ctx.fillStyle = rgba(T.paper, 1)
  ctx.fillRect(0, 0, W, H)

  drawBelowGround(ctx, scene, datum)
  drawStandingAxis(ctx, scene, datum)
  drawTimeAxis(ctx, scene, x)
  drawWindowEdge(ctx, scene, x)
  if (scene.showLinks) drawLinks(ctx, scene, places)
  drawItems(ctx, scene, places)
  drawFocus(ctx, scene, places)

  // The frame last, so nothing overdraws it.
  ctx.strokeStyle = rgba(T.rule, 1)
  ctx.lineWidth = 1
  ctx.strokeRect(MARGIN.left + 0.5, MARGIN.top + 0.5,
    plotW - 1, H - MARGIN.top - MARGIN.bottom - 1)
}

/**
 * The ground below the floor.
 *
 * Hatched rather than tinted. A wash of colour would read as another category of item; a hatch
 * reads as excluded ground, which is what it is — and it survives being printed in grey, which
 * a page of a briefing pack routinely is.
 */
function drawBelowGround(ctx: CanvasRenderingContext2D, scene: ChronoScene, datum: number) {
  const { theme: T, width: W, height: H } = scene
  const top = Math.max(MARGIN.top, datum)
  const bottom = H - MARGIN.bottom
  if (bottom <= top) return
  const left = MARGIN.left
  const right = W - MARGIN.right

  ctx.save()
  ctx.beginPath()
  ctx.rect(left, top, right - left, bottom - top)
  ctx.clip()
  ctx.fillStyle = rgba(T.below, 0.035)
  ctx.fillRect(left, top, right - left, bottom - top)
  ctx.strokeStyle = rgba(T.below, 0.13)
  ctx.lineWidth = 0.7
  for (let d = -(bottom - top); d < right - left; d += 7) {
    ctx.beginPath()
    ctx.moveTo(left + d, bottom)
    ctx.lineTo(left + d + (bottom - top), top)
    ctx.stroke()
  }
  ctx.restore()
}

function drawStandingAxis(ctx: CanvasRenderingContext2D, scene: ChronoScene, datum: number) {
  const { theme: T, width: W } = scene
  const { lo, hi } = extent(scene.items)

  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  for (let s = Math.ceil(lo); s <= Math.floor(hi); s++) {
    const y = standingY(scene, s)
    if (s !== 0) {
      ctx.strokeStyle = rgba(T.rule, 0.75)
      ctx.lineWidth = 0.6
      ctx.beginPath()
      ctx.moveTo(MARGIN.left, y + 0.5)
      ctx.lineTo(W - MARGIN.right, y + 0.5)
      ctx.stroke()
      ctx.font = '400 9px ui-monospace, "JetBrains Mono", Menlo, monospace'
      ctx.fillStyle = rgba(T.inkFaint, 1)
      ctx.fillText(s > 0 ? `+${s}` : String(s), MARGIN.left - 8, y)
    }
  }

  // The datum. Heavier than anything else on the figure, and named — a rule a reader has to
  // infer is a rule they will not trust when it matters.
  ctx.strokeStyle = rgba(T.ruleStrong, 0.9)
  ctx.lineWidth = 1.6
  ctx.beginPath()
  ctx.moveTo(MARGIN.left, datum + 0.5)
  ctx.lineTo(W - MARGIN.right, datum + 0.5)
  ctx.stroke()

  ctx.font = '500 9px ui-monospace, "JetBrains Mono", Menlo, monospace'
  ctx.fillStyle = rgba(T.ruleStrong, 1)
  ctx.fillText(`${scene.floor.reliability}${scene.floor.credibility}`, MARGIN.left - 8, datum)

  ctx.save()
  ctx.translate(11, (MARGIN.top + scene.height - MARGIN.bottom) / 2)
  ctx.rotate(-Math.PI / 2)
  ctx.textAlign = 'center'
  ctx.font = '400 8.5px ui-monospace, "JetBrains Mono", Menlo, monospace'
  ctx.fillStyle = rgba(T.inkFaint, 1)
  ctx.fillText('STEPS FROM THE FLOOR', 0, 0)
  ctx.restore()
}

function drawTimeAxis(
  ctx: CanvasRenderingContext2D, scene: ChronoScene, x: (at: number) => number,
) {
  const { theme: T, height: H } = scene
  const bottom = H - MARGIN.bottom
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  for (const tick of timeTicks(scene.domain)) {
    const tx = Math.round(x(tick.at)) + 0.5
    if (tx < MARGIN.left || tx > scene.width - MARGIN.right) continue
    ctx.strokeStyle = rgba(T.rule, tick.major ? 1 : 0.55)
    ctx.lineWidth = tick.major ? 0.9 : 0.6
    ctx.beginPath()
    ctx.moveTo(tx, MARGIN.top)
    ctx.lineTo(tx, bottom + (tick.major ? 6 : 3))
    ctx.stroke()
    ctx.font = `${tick.major ? 500 : 400} 9px ui-monospace, "JetBrains Mono", Menlo, monospace`
    ctx.fillStyle = rgba(tick.major ? T.inkDim : T.inkFaint, 1)
    ctx.fillText(tick.label, tx, bottom + 9)
  }
}

/**
 * Where the custody window begins.
 *
 * Items to the LEFT of this line were collected inside the window but describe something that
 * happened before it opened. That is not an error and it is not out of scope — it is the whole
 * point of separating the two dates, and a figure that clipped there would hide every piece of
 * history the archive has recently caught up on.
 */
function drawWindowEdge(
  ctx: CanvasRenderingContext2D, scene: ChronoScene, x: (at: number) => number,
) {
  const { theme: T, height: H } = scene
  const wx = x(scene.windowFrom)
  if (wx <= MARGIN.left + 2 || wx >= scene.width - MARGIN.right) return
  ctx.save()
  ctx.strokeStyle = rgba(T.inkFaint, 0.85)
  ctx.setLineDash([2, 3])
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(Math.round(wx) + 0.5, MARGIN.top)
  ctx.lineTo(Math.round(wx) + 0.5, H - MARGIN.bottom)
  ctx.stroke()
  ctx.setLineDash([])
  ctx.font = '400 8px ui-monospace, "JetBrains Mono", Menlo, monospace'
  ctx.fillStyle = rgba(T.inkFaint, 1)
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  ctx.fillText('CUSTODY WINDOW OPENS', wx + 4, MARGIN.top + 3)
  ctx.restore()
}

/**
 * Corroborations and disputes, as arcs between custody marks.
 *
 * A correction in this module is a NEW item pointing at the old one, never a rewrite — so
 * these arcs are the record's own account of changing its mind, and they are drawn between the
 * two custody dates because that is when the record actually changed.
 */
function drawLinks(
  ctx: CanvasRenderingContext2D, scene: ChronoScene, places: Placement[],
) {
  const { theme: T } = scene
  const by = new Map(places.map((p) => [p.id, p]))
  ctx.save()
  for (const link of scene.links) {
    const a = by.get(link.fromItemId)
    const b = by.get(link.toItemId)
    if (!a || !b) continue
    const dispute = link.kind === 'DISPUTES'
    // An arc fades when EITHER end is outside the filter: a link half of which the reader has
    // filtered away is not a fact about what they asked for.
    const faded = scene.missed !== null
      && (scene.missed.has(link.fromItemId) || scene.missed.has(link.toItemId))
    ctx.strokeStyle = rgba(dispute ? T.dispute : T.corroborate, faded ? 0.1 : 0.5)
    ctx.lineWidth = 1
    if (dispute) ctx.setLineDash([3, 2])
    const midX = (a.cx + b.cx) / 2
    // Arc height rides on the horizontal distance, so a correction filed years later reads as
    // a longer reach across the record than one filed the same afternoon.
    const lift = Math.min(60, 12 + Math.abs(b.cx - a.cx) * 0.16)
    ctx.beginPath()
    ctx.moveTo(a.cx, a.y)
    ctx.quadraticCurveTo(midX, Math.min(a.y, b.y) - lift, b.cx, b.y)
    ctx.stroke()
    ctx.setLineDash([])
  }
  ctx.restore()
}

function drawItems(
  ctx: CanvasRenderingContext2D, scene: ChronoScene, places: Placement[],
) {
  const { theme: T } = scene
  for (let i = 0; i < scene.items.length; i++) {
    const item = scene.items[i]
    const p = places[i]
    if (!item || !p) continue
    if (p.cx < MARGIN.left - 40 || p.cx > scene.width - MARGIN.right + 40) continue

    // A filter DIMS what it did not match rather than removing it, so the reader can still see
    // how much of the record their question is actually describing.
    const missed = scene.missed !== null && scene.missed.has(item.id)
    const alpha = missed ? 0.1 : 1
    const above = item.standing >= 0
    const contested = item.veracityCode === 'DISPUTED' || item.veracityCode === 'DEBUNKED'
    const ink = contested ? T.dispute : above ? T.above : T.below

    ctx.save()
    if (p.ex !== null && scene.showLigatures) {
      // The ligature. Its LENGTH is the fact; it is drawn thin so a dense figure reads as a
      // texture of delays rather than as a mat of lines.
      ctx.strokeStyle = rgba(ink, 0.34 * alpha)
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(p.ex, p.y)
      ctx.lineTo(p.cx, p.y)
      ctx.stroke()

      ctx.fillStyle = rgba(ink, 0.85 * alpha)
      ctx.beginPath()
      ctx.arc(p.ex, p.y, 2.2, 0, Math.PI * 2)
      ctx.fill()
    } else if (p.ex === null) {
      // No event date: a barb pointing off into the unknown, never a dot at zero delay.
      ctx.strokeStyle = rgba(T.inkFaint, 0.75 * alpha)
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(p.cx - 9, p.y)
      ctx.lineTo(p.cx - 3, p.y)
      ctx.moveTo(p.cx - 9, p.y)
      ctx.lineTo(p.cx - 6, p.y - 2.4)
      ctx.moveTo(p.cx - 9, p.y)
      ctx.lineTo(p.cx - 6, p.y + 2.4)
      ctx.stroke()
    }

    // Custody: an OPEN mark, always. The distinction between the two ends has to survive
    // greyscale and a small size, so it is a fill against a ring rather than two colours.
    ctx.strokeStyle = rgba(ink, 0.95 * alpha)
    ctx.lineWidth = item.redacted ? 1.4 : 1.2
    if (item.redacted) ctx.setLineDash([1.6, 1.6])
    ctx.fillStyle = rgba(T.paper, alpha)
    ctx.beginPath()
    ctx.arc(p.cx, p.y, 3.1, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    ctx.setLineDash([])

    if (contested) {
      ctx.strokeStyle = rgba(T.dispute, 0.95 * alpha)
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(p.cx - 2.2, p.y - 2.2)
      ctx.lineTo(p.cx + 2.2, p.y + 2.2)
      ctx.moveTo(p.cx + 2.2, p.y - 2.2)
      ctx.lineTo(p.cx - 2.2, p.y + 2.2)
      ctx.stroke()
    }
    ctx.restore()
  }
}

/** The selected item, with guides to both axes — a reading, not a highlight. */
function drawFocus(
  ctx: CanvasRenderingContext2D, scene: ChronoScene, places: Placement[],
) {
  const { theme: T } = scene
  const id = scene.hovered ?? scene.selected
  if (!id) return
  const index = scene.items.findIndex((item) => item.id === id)
  const p = places[index]
  const item = scene.items[index]
  if (!p || !item) return

  ctx.save()
  ctx.strokeStyle = rgba(T.ink, 0.28)
  ctx.setLineDash([1, 3])
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(MARGIN.left, p.y + 0.5)
  ctx.lineTo(scene.width - MARGIN.right, p.y + 0.5)
  ctx.moveTo(Math.round(p.cx) + 0.5, MARGIN.top)
  ctx.lineTo(Math.round(p.cx) + 0.5, scene.height - MARGIN.bottom)
  ctx.stroke()
  ctx.setLineDash([])

  ctx.strokeStyle = rgba(T.ink, 0.9)
  ctx.lineWidth = 1.2
  ctx.beginPath()
  ctx.arc(p.cx, p.y, 7, 0, Math.PI * 2)
  ctx.stroke()
  if (p.ex !== null) {
    ctx.beginPath()
    ctx.arc(p.ex, p.y, 5, 0, Math.PI * 2)
    ctx.stroke()
    // The ligature of the focused item, restated at full weight: this is the measurement the
    // reader came for.
    ctx.lineWidth = 1.6
    ctx.beginPath()
    ctx.moveTo(p.ex, p.y)
    ctx.lineTo(p.cx, p.y)
    ctx.stroke()
  }
  ctx.restore()
}

/** Nearest item to a point, within a forgiving radius. Custody mark first, then the event. */
export function hitTest(places: Placement[], sx: number, sy: number): string | null {
  let best: { id: string, d: number } | null = null
  for (const p of places) {
    const dc = Math.hypot(p.cx - sx, p.y - sy)
    if (dc <= 9 && (!best || dc < best.d)) best = { id: p.id, d: dc }
    if (p.ex !== null) {
      const de = Math.hypot(p.ex - sx, p.y - sy)
      if (de <= 9 && (!best || de < best.d)) best = { id: p.id, d: de }
    }
  }
  return best?.id ?? null
}

/**
 * The density strip under the figure — the brush's own map.
 *
 * Collections per bucket over the domain, so the reader can see where the record is thick
 * before deciding what to select. Drawn from the FULL item set, never the filtered one: a
 * brush whose map redrew itself as you brushed would be a control you could not aim.
 */
export function drawDensity(
  ctx: CanvasRenderingContext2D, items: AssayItem[], domain: TimeDomain,
  width: number, height: number, dpr: number, theme: Theme,
  selection: { from: number, to: number } | null,
) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, width, height)
  const plotW = width - MARGIN.left - MARGIN.right
  const span = domain.to - domain.from || 1
  const buckets = Math.max(20, Math.min(240, Math.floor(plotW / 4)))
  const counts = new Array<number>(buckets).fill(0)
  for (const item of items) {
    const at = Date.parse(item.collectedAt)
    if (!Number.isFinite(at)) continue
    const b = Math.floor(((at - domain.from) / span) * buckets)
    if (b >= 0 && b < buckets) counts[b] = (counts[b] ?? 0) + 1
  }
  const peak = Math.max(1, ...counts)

  ctx.fillStyle = rgba(theme.paper, 1)
  ctx.fillRect(0, 0, width, height)
  const w = plotW / buckets
  for (let i = 0; i < buckets; i++) {
    const h = ((counts[i] ?? 0) / peak) * (height - 5)
    if (h <= 0) continue
    ctx.fillStyle = rgba(theme.inkDim, 0.5)
    ctx.fillRect(MARGIN.left + i * w, height - 2 - h, Math.max(1, w - 0.5), h)
  }
  ctx.strokeStyle = rgba(theme.rule, 1)
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(MARGIN.left, height - 1.5)
  ctx.lineTo(width - MARGIN.right, height - 1.5)
  ctx.stroke()

  if (selection) {
    const x = (at: number) => MARGIN.left + ((at - domain.from) / span) * plotW
    const a = x(selection.from)
    const b = x(selection.to)
    ctx.fillStyle = rgba(theme.above, 0.16)
    ctx.fillRect(a, 0, b - a, height)
    ctx.strokeStyle = rgba(theme.above, 0.85)
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(Math.round(a) + 0.5, 0)
    ctx.lineTo(Math.round(a) + 0.5, height)
    ctx.moveTo(Math.round(b) + 0.5, 0)
    ctx.lineTo(Math.round(b) + 0.5, height)
    ctx.stroke()
  }
}

/** Screen x → instant, for the brush. */
export function timeAt(domain: TimeDomain, width: number, sx: number): number {
  const plotW = width - MARGIN.left - MARGIN.right
  const t = (sx - MARGIN.left) / (plotW || 1)
  return domain.from + Math.max(0, Math.min(1, t)) * (domain.to - domain.from)
}
